import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Tooltip,
  Typography
} from "@material-ui/core";
import { FavoriteBorder as FavoriteBorderIcon } from "@material-ui/icons";
import { useTranslation } from "react-i18next";

import useAuth from "hooks/useAuth";
import { getFavoritesListingsQuery } from "utils/Haim/firestoreUtils";
import { pickMinisitePage } from "utils/minisite/minisitePage";
import { fetchListingsByIds } from "utils/listings/fetchListings";
import { CondensedListingRowContent } from "./NewListingPicker";

import type {
  FavoriteListingsOfContact,
  ProcessedListingData
} from "../../../@types/sharedSchema";

// Filter set surfaced in the modal. Mirrors the chips in
// `ListingMatchFavoriteListings` so the realtor sees the same vocabulary
// here as in the contact's favorites screen. `sawPage`/`didntSawPage`
// are intentionally NOT included — they require the page-view data hook,
// which would mean an extra fetch on every modal open for a filter that
// rarely changes contract-relevant decisions.
const CONTACT_LISTING_FILTERS = [
  "hasNotes",
  "liked",
  "disliked",
  "visited",
  "notVisited"
] as const;
type ContactListingFilter = typeof CONTACT_LISTING_FILTERS[number];
const FILTER_PAIRS: [ContactListingFilter, ContactListingFilter][] = [
  ["liked", "disliked"],
  ["visited", "notVisited"]
];

export type FavoriteListingsModalButtonProps = {
  contactId: string;
  /** Friendly name shown in the modal title (e.g. "John Doe"). Empty
   *  string is fine — the title just falls back to the generic form. */
  contactName: string;
  /** Currently picked listings in the parent form — used to (a) show
   *  already-added favorites as disabled-checked rows so the realtor
   *  knows they're already in the document, and (b) compute the
   *  "Add (N)" count of net-new additions. */
  selectedListings: ProcessedListingData[];
  /** Called with the listings to APPEND to the existing selection.
   *  Parent is responsible for merging with `selectedListings` (and any
   *  single-listing-mode cap). The modal only emits the *new* picks. */
  onAddListings: (newListings: ProcessedListingData[]) => void;
};

/**
 * Text button + dialog for quickly adding a contact's favorited listings
 * into the signature form's listings selection. Loads the contact's
 * `favorite-listings-of-contact` doc on first open, hydrates the listing
 * IDs via Algolia, and renders a slim selectable list. Listings already
 * in the parent selection appear pre-checked + disabled — the modal is
 * additive, never destructive (closing it or clicking Cancel leaves the
 * parent's selection untouched).
 */
export default function FavoriteListingsModalButton({
  contactId,
  contactName,
  selectedListings,
  onAddListings
}: FavoriteListingsModalButtonProps): JSX.Element {
  const { t } = useTranslation();
  const { user } = useAuth();

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // `null` = not yet fetched; `[]` = fetched, contact has no favorites.
  const [favoriteListings, setFavoriteListings] = useState<
    ProcessedListingData[] | null
  >(null);
  // Full favorites doc kept around so the filter chips can read the
  // contact's liked / disliked / visited sets and per-listing notes.
  const [favoriteDoc, setFavoriteDoc] = useState<FavoriteListingsOfContact | null>(
    null
  );
  const [activeFilters, setActiveFilters] = useState<ContactListingFilter[]>([]);

  // IDs the realtor has *newly* checked inside the modal (not counting
  // already-picked ones). Reset every time the modal opens so stale picks
  // from a prior open don't survive a Cancel.
  const [stagedIds, setStagedIds] = useState<Set<string>>(new Set());

  const selectedIds = useMemo(
    () =>
      new Set(
        selectedListings
          .map((l) => l.id)
          .filter((id): id is string => !!id)
      ),
    [selectedListings]
  );

  // Reset (favorites list + staged) whenever the modal is opened or the
  // contact changes. A new open always triggers a fresh fetch so the
  // realtor sees current data; a stale cache is worse than a 1s spinner.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setStagedIds(new Set());
    setActiveFilters([]);
    setError(null);
    setFavoriteListings(null);
    setFavoriteDoc(null);
    setLoading(true);

    (async () => {
      try {
        const snap = await getFavoritesListingsQuery(user.email, contactId).get();
        if (cancelled) return;
        const doc = pickMinisitePage(snap.docs)?.data() ?? null;
        setFavoriteDoc(doc);
        const listingIds = (doc?.listingIds ?? []).filter(Boolean);
        if (listingIds.length === 0) {
          setFavoriteListings([]);
          return;
        }
        // `fetchListingsByIds` resolves each ID via Firestore and drops
        // misses (e.g. listings that were since deleted), so a partial
        // favorites list is fine — the realtor just sees fewer rows than
        // total IDs.
        const hits = await fetchListingsByIds(listingIds);
        if (cancelled) return;
        setFavoriteListings(
          hits.map((h) => ({ ...h, id: h.objectID })) as ProcessedListingData[]
        );
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Failed to load contact favorites", err);
        if (!cancelled) {
          setError(t("signatures.newDoc.favorites.loadError") as string);
          setFavoriteListings([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, contactId, user.email, t]);

  // Toggle a filter chip on/off. Picking one side of a pair (e.g.
  // "visited") removes the other ("notVisited") — same as the contact
  // favorites screen, where a single listing can't be both.
  const toggleFilter = (filter: ContactListingFilter) => {
    setActiveFilters((prev) => {
      if (prev.includes(filter)) return prev.filter((f) => f !== filter);
      const pair = FILTER_PAIRS.find((p) => p.includes(filter));
      const opposite = pair?.find((f) => f !== filter);
      const withoutOpposite = opposite ? prev.filter((f) => f !== opposite) : prev;
      return [...withoutOpposite, filter];
    });
  };

  /** Listings after filter chips have been applied. Empty result with
   *  active filters is rendered as a "no results" message below; an
   *  unfiltered empty list is handled by the existing "no favorites yet"
   *  state, so the two empty cases stay distinct. */
  const filteredFavoriteListings = useMemo(() => {
    if (!favoriteListings) return null;
    if (activeFilters.length === 0 || !favoriteDoc) return favoriteListings;
    return favoriteListings.filter((l) => {
      const id = l.id;
      if (!id) return false;
      const hasNotes = !!favoriteDoc.additionalDetails?.[id]?.listingNotes;
      const isLiked = !!favoriteDoc.listingIdsUserLiked?.includes(id);
      const isDisliked = !!favoriteDoc.listingIdsUserDisliked?.includes(id);
      const isVisited = !!favoriteDoc.listingIdsUserVisited?.includes(id);
      return activeFilters.every((filter) => {
        switch (filter) {
          case "hasNotes":
            return hasNotes;
          case "liked":
            return isLiked;
          case "disliked":
            return isDisliked;
          case "visited":
            return isVisited;
          case "notVisited":
            return !isVisited;
          default:
            return true;
        }
      });
    });
  }, [favoriteListings, activeFilters, favoriteDoc]);

  const toggleStaged = (listingId: string) => {
    setStagedIds((prev) => {
      const next = new Set(prev);
      if (next.has(listingId)) next.delete(listingId);
      else next.add(listingId);
      return next;
    });
  };

  const handleAdd = () => {
    const favorites = favoriteListings ?? [];
    const additions = favorites.filter(
      (l) => l.id && stagedIds.has(l.id) && !selectedIds.has(l.id)
    );
    if (additions.length > 0) onAddListings(additions);
    setOpen(false);
  };

  return (
    <>
      <Button
        size="small"
        variant="text"
        startIcon={<FavoriteBorderIcon fontSize="small" />}
        onClick={() => setOpen(true)}
        sx={{ textTransform: "none", flexShrink: 0 }}
      >
        {t("signatures.newDoc.favorites.button")}
      </Button>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6">
            {t("signatures.newDoc.favorites.title")}
          </Typography>
          {contactName && (
            <Typography variant="caption" color="text.secondary">
              {contactName}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers sx={{ p: 1 }}>
          {/* Filter chips. Shown once we know the contact actually has
              favorites — surfacing chips above an empty list would just
              be visual noise. Layout mirrors `ListingMatchFavoriteListings`
              so the realtor sees the same vocabulary in both places. */}
          {!loading && !error && favoriteListings && favoriteListings.length > 0 && (
            <Stack
              direction="row"
              alignItems="center"
              flexWrap="wrap"
              gap={0.75}
              sx={{ px: 1, pt: 1, pb: 1.25 }}
            >
              <Typography variant="caption" color="text.secondary">
                {t("listingsMatch.filterBy.title")}:
              </Typography>
              <Chip
                size="small"
                label={t("listingsMatch.filterBy.hasNotes")}
                variant={activeFilters.includes("hasNotes") ? "filled" : "outlined"}
                color={activeFilters.includes("hasNotes") ? "primary" : "default"}
                onClick={() => toggleFilter("hasNotes")}
              />
              {FILTER_PAIRS.map(([a, b]) => (
                <Stack key={a} direction="row" gap={0.5}>
                  <Chip
                    size="small"
                    label={t(`listingsMatch.filterBy.${a}`)}
                    variant={activeFilters.includes(a) ? "filled" : "outlined"}
                    color={activeFilters.includes(a) ? "primary" : "default"}
                    onClick={() => toggleFilter(a)}
                  />
                  <Chip
                    size="small"
                    label={t(`listingsMatch.filterBy.${b}`)}
                    variant={activeFilters.includes(b) ? "filled" : "outlined"}
                    color={activeFilters.includes(b) ? "primary" : "default"}
                    onClick={() => toggleFilter(b)}
                  />
                </Stack>
              ))}
            </Stack>
          )}
          {loading && (
            <Stack alignItems="center" sx={{ py: 4 }}>
              <CircularProgress size={28} />
            </Stack>
          )}
          {!loading && error && (
            <Alert severity="error" sx={{ m: 1 }}>
              {error}
            </Alert>
          )}
          {!loading && !error && favoriteListings && favoriteListings.length === 0 && (
            <Stack alignItems="center" sx={{ py: 4 }}>
              <Typography variant="body2" color="text.secondary">
                {t("signatures.newDoc.favorites.empty")}
              </Typography>
            </Stack>
          )}
          {/* Filters active but nothing matches — distinct from the
              "no favorites yet" state above, so the realtor can tell
              whether to clear filters vs. ask the contact to favorite
              some listings. */}
          {!loading &&
            !error &&
            favoriteListings &&
            favoriteListings.length > 0 &&
            filteredFavoriteListings &&
            filteredFavoriteListings.length === 0 && (
              <Stack alignItems="center" sx={{ py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  {t("listingsMatch.filterBy.noResults")}
                </Typography>
              </Stack>
            )}
          {!loading &&
            !error &&
            filteredFavoriteListings &&
            filteredFavoriteListings.length > 0 && (
            <Stack>
              {filteredFavoriteListings.map((l) => {
                const listingId = l.id;
                if (!listingId) return null;
                const isAlreadyPicked = selectedIds.has(listingId);
                const isStaged = stagedIds.has(listingId);
                // Already-picked rows show as checked-and-locked so the
                // realtor sees "this is in the document" without us
                // letting the modal drift out of sync with the parent's
                // selection if they untick it here.
                const checked = isAlreadyPicked || isStaged;
                const row = (
                  <Stack
                    direction="row"
                    alignItems="center"
                    onClick={
                      isAlreadyPicked ? undefined : () => toggleStaged(listingId)
                    }
                    sx={{
                      px: 1,
                      py: 0.5,
                      gap: 1.25,
                      borderRadius: 1,
                      cursor: isAlreadyPicked ? "default" : "pointer",
                      opacity: isAlreadyPicked ? 0.65 : 1,
                      "&:hover": {
                        bgcolor: isAlreadyPicked ? undefined : "action.hover"
                      }
                    }}
                  >
                    <Checkbox
                      size="small"
                      checked={checked}
                      disabled={isAlreadyPicked}
                      onChange={() => toggleStaged(listingId)}
                      onClick={(e) => e.stopPropagation()}
                      sx={{ p: 0.5 }}
                    />
                    <CondensedListingRowContent listing={l} translate={t} />
                  </Stack>
                );
                return (
                  <Box key={listingId}>
                    {isAlreadyPicked ? (
                      <Tooltip
                        title={t("signatures.newDoc.favorites.alreadyAdded") as string}
                        placement="top-start"
                      >
                        {row}
                      </Tooltip>
                    ) : (
                      row
                    )}
                  </Box>
                );
              })}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} variant="text">
            {t("signatures.newDoc.favorites.cancel")}
          </Button>
          <Button
            onClick={handleAdd}
            variant="contained"
            // Disable when nothing new has been ticked — clicking would
            // be a noop, and an active button there confuses the realtor
            // into thinking they're committing something.
            disabled={
              loading ||
              !favoriteListings ||
              [...stagedIds].every((id) => selectedIds.has(id))
            }
          >
            {t("signatures.newDoc.favorites.add", {
              count: [...stagedIds].filter((id) => !selectedIds.has(id)).length
            })}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
